import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import {
  ArrowLeft,
  Droplets,
  CheckCircle2,
  XCircle,
  Search,
  Waves,
  Thermometer,
  ShieldCheck,
  FlaskConical,
  Loader2,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { ComparisonBarChart, DistributionPieChart, StatCard } from '../../components/analytics';
import { apiGet } from '../../../../shared/services/apiClient';

// ─── API Types ─────────────────────────────────────────────────────────────────
interface WaterSummary {
  totalSchools: number;
  drinkingWater: number;
  waterPurified: number;
  coldWater: number;
  waterStorage: number;
  borewellGroundWater: number;
  mixedSource: number;
  municipalSupply: number;
}

interface RoWiseDetail {
  roID: number;
  roName: string;
  totalSchools: number;
  drinkingWater: string | number;
  waterPurified: string | number;
  coldWater: string | number;
  waterStorage: string | number;
  borewellGroundWater: string | number;
  mixedSource: string | number;
  municipalSupply: string | number;
}

interface SchoolDetail {
  schoolID: number;
  schoolName: string;
  roName: string;
  districtName: string;
  stateName: string;
  isPMShree: number;
  drinkingWaterFacility: number;   // 1 = YES, 0 = NO
  waterSource: number;              // 1 = Borewell, 2 = Mixed, 3 = Municipal
  waterPurification: number;
  coldWaterFacility: number;
  warmWaterFacility: number;
  waterStorageFacility: number;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
const WATER_SOURCE_LABELS: Record<number, string> = {
  1: 'Borewell / Ground Water',
  2: 'Mixed Source',
  3: 'Municipal Supply',
};

const yn = (v: number) => (v === 1 ? 'YES' : 'NO');

const PAGE_SIZE = 20;

// ─── Component ─────────────────────────────────────────────────────────────────
const WaterProfileAnalysis: React.FC = () => {
  const navigate = useNavigate();

  // ── State ──
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<WaterSummary | null>(null);
  const [roWise, setRoWise] = useState<RoWiseDetail[]>([]);
  const [details, setDetails] = useState<SchoolDetail[]>([]);

  // Filters & pagination
  const [search, setSearch] = useState('');
  const [filterRO, setFilterRO] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [page, setPage] = useState(1);

  // ── Fetch ──
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet('/kvis/miscellaneous/summary');
      if (res?.success && res?.data) {
        setSummary(res.data.summary ?? null);
        setRoWise(res.data.roWiseDetails ?? []);
        setDetails(res.data.details ?? []);
      } else {
        setError('Failed to load data. Please try again.');
      }
    } catch {
      setError('Network error. Could not reach the server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // ── Chart: Pie – Water Sources distribution from summary ──
  const pieData = useMemo(() => {
    if (!summary) return [];
    return [
      { name: 'Borewell / Ground Water', value: summary.borewellGroundWater },
      { name: 'Mixed Source',            value: summary.mixedSource },
      { name: 'Municipal Supply',        value: summary.municipalSupply },
    ].filter(d => d.value > 0);
  }, [summary]);

  // ── Chart: Bar – RO-wise Purification vs Cold Water ──
  const barData = useMemo(() => {
    return roWise.map(ro => ({
      category: ro.roName,
      pmshriCount: Number(ro.waterPurified),
      nonPmshriCount: Number(ro.coldWater),
      total: ro.totalSchools,
      pmshriPercentage: ro.totalSchools > 0
        ? Math.round((Number(ro.waterPurified) / ro.totalSchools) * 100)
        : 0,
    }));
  }, [roWise]);

  // ── Unique RO list for filter dropdown ──
  const roOptions = useMemo(() => {
    const unique = Array.from(new Set(details.map(d => d.roName))).sort();
    return unique;
  }, [details]);

  // ── Filtered + paginated details table ──
  const filteredDetails = useMemo(() => {
    const q = search.toLowerCase();
    return details.filter(s => {
      const matchSearch =
        s.schoolName.toLowerCase().includes(q) ||
        s.roName.toLowerCase().includes(q) ||
        s.stateName.toLowerCase().includes(q) ||
        s.districtName.toLowerCase().includes(q);
      const matchRO = filterRO ? s.roName === filterRO : true;
      const matchSource = filterSource
        ? String(s.waterSource) === filterSource
        : true;
      return matchSearch && matchRO && matchSource;
    });
  }, [details, search, filterRO, filterSource]);

  const totalPages = Math.ceil(filteredDetails.length / PAGE_SIZE);
  const pagedDetails = filteredDetails.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // reset to page 1 on filter change
  useEffect(() => { setPage(1); }, [search, filterRO, filterSource]);

  // ── Render ──
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="flex-1 container mx-auto px-4 py-6">
        <Breadcrumb />

        {/* ─── Title Banner ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-200 transition-colors">
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <Droplets className="h-7 w-7 text-teal-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Drinking Water Profile Analysis</h1>
              <p className="text-sm text-gray-500">Water Availability, Sources, Purification &amp; Storage across KVs</p>
            </div>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        {/* ─── Loading ───────────────────────────────────────────────────────────── */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <Loader2 className="h-10 w-10 text-teal-600 animate-spin" />
            <p className="text-gray-500 text-sm">Loading water profile data…</p>
          </div>
        )}

        {/* ─── Error ────────────────────────────────────────────────────────────── */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <AlertTriangle className="h-10 w-10 text-red-400" />
            <p className="text-gray-600 font-medium">{error}</p>
            <button
              onClick={fetchData}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* ─── Main Content ──────────────────────────────────────────────────────── */}
        {!loading && !error && summary && (
          <>
            {/* ─── Summary Cards ──────────────────────────────────────────────── */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
              <StatCard
                title="Total Schools"
                value={summary.totalSchools.toLocaleString('en-IN')}
                icon={<Waves className="h-5 w-5 text-teal-600" />}
                color="teal"
                subtitle="All KVs"
              />
              <StatCard
                title="Drinking Water"
                value={summary.drinkingWater.toLocaleString('en-IN')}
                icon={<Droplets className="h-5 w-5 text-blue-600" />}
                color="blue"
                subtitle={`${Math.round((summary.drinkingWater / summary.totalSchools) * 100)}% schools`}
              />
              <StatCard
                title="Water Purified"
                value={summary.waterPurified.toLocaleString('en-IN')}
                icon={<FlaskConical className="h-5 w-5 text-cyan-600" />}
                color="cyan"
                subtitle="Purification systems"
              />
              <StatCard
                title="Cold Water"
                value={summary.coldWater.toLocaleString('en-IN')}
                icon={<Thermometer className="h-5 w-5 text-indigo-600" />}
                color="indigo"
                subtitle="Cold water access"
              />
              <StatCard
                title="Water Storage"
                value={summary.waterStorage.toLocaleString('en-IN')}
                icon={<ShieldCheck className="h-5 w-5 text-green-600" />}
                color="green"
                subtitle="Storage tanks"
              />
              <StatCard
                title="Borewell"
                value={summary.borewellGroundWater.toLocaleString('en-IN')}
                icon={<Droplets className="h-5 w-5 text-amber-600" />}
                color="amber"
                subtitle="Ground water"
              />
              <StatCard
                title="Municipal"
                value={summary.municipalSupply.toLocaleString('en-IN')}
                icon={<Waves className="h-5 w-5 text-purple-600" />}
                color="purple"
                subtitle="Municipal supply"
              />
            </div>

            {/* ─── Charts ─────────────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                <h3 className="font-semibold text-gray-700 mb-4">Water Sources Distribution</h3>
                <DistributionPieChart data={pieData} title="Source Breakdown" />
              </div>

              <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                <h3 className="font-semibold text-gray-700 mb-1">Region-wise: Purification vs Cold Water</h3>
                <p className="text-xs text-gray-400 mb-3">Blue = Purified &nbsp;|&nbsp; Orange = Cold Water</p>
                <ComparisonBarChart
                  data={barData}
                  title="RO-wise Water Analytics"
                  xAxisLabel="Region"
                />
              </div>
            </div>

            {/* ─── RO-wise Summary Table ───────────────────────────────────────── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
              <div className="bg-teal-700 px-5 py-3">
                <h3 className="font-semibold text-white text-sm">Region-wise (RO) Water Summary</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-teal-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-teal-800 font-semibold">RO Name</th>
                      <th className="px-4 py-3 text-center text-teal-800 font-semibold">Total</th>
                      <th className="px-4 py-3 text-center text-teal-800 font-semibold">Drinking Water</th>
                      <th className="px-4 py-3 text-center text-teal-800 font-semibold">Purified</th>
                      <th className="px-4 py-3 text-center text-teal-800 font-semibold">Cold Water</th>
                      <th className="px-4 py-3 text-center text-teal-800 font-semibold">Storage</th>
                      <th className="px-4 py-3 text-center text-teal-800 font-semibold">Borewell</th>
                      <th className="px-4 py-3 text-center text-teal-800 font-semibold">Mixed</th>
                      <th className="px-4 py-3 text-center text-teal-800 font-semibold">Municipal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roWise.map((ro, idx) => (
                      <tr key={ro.roID} className={idx % 2 === 0 ? 'bg-white' : 'bg-teal-50/30'}>
                        <td className="px-4 py-2.5 font-semibold text-gray-800 whitespace-nowrap">{ro.roName}</td>
                        <td className="px-4 py-2.5 text-center text-gray-700 font-medium">{ro.totalSchools}</td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${Number(ro.drinkingWater) === ro.totalSchools ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {Number(ro.drinkingWater)}/{ro.totalSchools}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-center text-gray-600">{Number(ro.waterPurified)}</td>
                        <td className="px-4 py-2.5 text-center text-gray-600">{Number(ro.coldWater)}</td>
                        <td className="px-4 py-2.5 text-center text-gray-600">{Number(ro.waterStorage)}</td>
                        <td className="px-4 py-2.5 text-center text-gray-600">{Number(ro.borewellGroundWater)}</td>
                        <td className="px-4 py-2.5 text-center text-gray-600">{Number(ro.mixedSource)}</td>
                        <td className="px-4 py-2.5 text-center text-gray-600">{Number(ro.municipalSupply)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ─── School-wise Details ─────────────────────────────────────────── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
              {/* Header + filters */}
              <div className="bg-[#0d7c6b] px-5 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-white">School-wise Water Infrastructure Details</h3>
                  <p className="text-xs text-white/70 mt-0.5">{filteredDetails.length} schools</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {/* Search */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search school / state / district…"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      className="text-sm pl-8 pr-3 py-1.5 rounded-lg border-0 bg-white/20 text-white placeholder-white/60 focus:outline-none focus:bg-white/30 w-56"
                    />
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/60 pointer-events-none" />
                  </div>

                  {/* RO filter */}
                  <select
                    value={filterRO}
                    onChange={e => setFilterRO(e.target.value)}
                    className="text-sm px-3 py-1.5 rounded-lg bg-white/20 text-white border-0 focus:outline-none focus:bg-white/30"
                  >
                    <option value="">All Regions</option>
                    {roOptions.map(ro => (
                      <option key={ro} value={ro} className="text-gray-800">{ro}</option>
                    ))}
                  </select>

                  {/* Source filter */}
                  <select
                    value={filterSource}
                    onChange={e => setFilterSource(e.target.value)}
                    className="text-sm px-3 py-1.5 rounded-lg bg-white/20 text-white border-0 focus:outline-none focus:bg-white/30"
                  >
                    <option value="">All Sources</option>
                    <option value="1" className="text-gray-800">Borewell / Ground Water</option>
                    <option value="2" className="text-gray-800">Mixed Source</option>
                    <option value="3" className="text-gray-800">Municipal Supply</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-teal-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-teal-800 font-semibold">#</th>
                      <th className="px-4 py-3 text-left text-teal-800 font-semibold">School Name</th>
                      <th className="px-4 py-3 text-left text-teal-800 font-semibold">RO / State</th>
                      <th className="px-4 py-3 text-left text-teal-800 font-semibold">District</th>
                      <th className="px-4 py-3 text-center text-teal-800 font-semibold">PM Shree</th>
                      <th className="px-4 py-3 text-center text-teal-800 font-semibold">Drinking Water</th>
                      <th className="px-4 py-3 text-left text-teal-800 font-semibold">Water Source</th>
                      <th className="px-4 py-3 text-center text-teal-800 font-semibold">Purification</th>
                      <th className="px-4 py-3 text-center text-teal-800 font-semibold">Cold Water</th>
                      <th className="px-4 py-3 text-center text-teal-800 font-semibold">Warm Water</th>
                      <th className="px-4 py-3 text-center text-teal-800 font-semibold">Storage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedDetails.map((s, idx) => (
                      <tr key={s.schoolID} className={idx % 2 === 0 ? 'bg-white' : 'bg-teal-50/25'}>
                        <td className="px-4 py-2.5 text-gray-400 font-medium text-xs">
                          {(page - 1) * PAGE_SIZE + idx + 1}
                        </td>
                        <td className="px-4 py-2.5 font-semibold text-gray-800 max-w-xs">
                          <span className="line-clamp-2">{s.schoolName}</span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="text-gray-700 block text-xs font-semibold">{s.roName}</span>
                          <span className="text-xs text-gray-400">{s.stateName}</span>
                        </td>
                        <td className="px-4 py-2.5 text-gray-500 text-xs">{s.districtName}</td>
                        <td className="px-4 py-2.5 text-center">
                          {s.isPMShree ? (
                            <span className="inline-block px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold">PM Shree</span>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          {s.drinkingWaterFacility === 1
                            ? <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                            : <XCircle className="h-4 w-4 text-red-400 mx-auto" />}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                            s.waterSource === 3 ? 'bg-blue-50 text-blue-700' :
                            s.waterSource === 1 ? 'bg-amber-50 text-amber-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {WATER_SOURCE_LABELS[s.waterSource] ?? '—'}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${s.waterPurification === 1 ? 'bg-teal-50 text-teal-700 border border-teal-200' : 'bg-gray-100 text-gray-400'}`}>
                            {yn(s.waterPurification)}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${s.coldWaterFacility === 1 ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-400'}`}>
                            {yn(s.coldWaterFacility)}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${s.warmWaterFacility === 1 ? 'bg-orange-50 text-orange-700 border border-orange-200' : 'bg-gray-100 text-gray-400'}`}>
                            {yn(s.warmWaterFacility)}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${s.waterStorageFacility === 1 ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-400'}`}>
                            {yn(s.waterStorageFacility)}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {pagedDetails.length === 0 && (
                      <tr>
                        <td colSpan={11} className="px-4 py-12 text-center text-gray-400">
                          No schools matched your filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
                  <p className="text-xs text-gray-500">
                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredDetails.length)} of {filteredDetails.length}
                  </p>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-3 py-1 rounded text-xs font-medium bg-white border border-gray-200 text-gray-600 hover:bg-teal-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      ← Prev
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
                      const p = startPage + i;
                      return (
                        <button
                          key={p}
                          onClick={() => setPage(p)}
                          className={`w-8 h-7 rounded text-xs font-medium border ${
                            p === page
                              ? 'bg-teal-600 text-white border-teal-600'
                              : 'bg-white border-gray-200 text-gray-600 hover:bg-teal-50'
                          }`}
                        >
                          {p}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="px-3 py-1 rounded text-xs font-medium bg-white border border-gray-200 text-gray-600 hover:bg-teal-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ─── Bottom Nav ─────────────────────────────────────────────────────────── */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-[#6A1786] mb-4">Detailed Analysis Reports</h3>
          <p className="text-sm text-gray-600 mb-4">Access comprehensive analysis reports for each category</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { name: 'Infrastructure',  path: '/kvs-samagam/analytics/infrastructure',   icon: '🏫', color: 'blue' },
              { name: 'Enrollment',      path: '/kvs-samagam/analytics/enrollment',        icon: '👨‍🎓', color: 'green' },
              { name: 'ICT Profile',     path: '/kvs-samagam/analytics/ict-profile',       icon: '💻', color: 'purple' },
              { name: 'Library Profile', path: '/kvs-samagam/analytics/library-profile',   icon: '📚', color: 'orange' },
              { name: 'CWSN Profile',    path: '/kvs-samagam/analytics/cwsn-profile',      icon: '♿', color: 'pink' },
              { name: 'NCC Profile',     path: '/kvs-samagam/analytics/ncc-profile',       icon: '🎖️', color: 'cyan' },
              { name: 'Sports',          path: '/kvs-samagam/analytics/sports',            icon: '🏆', color: 'yellow' },
              { name: 'Green Building',  path: '/kvs-samagam/analytics/green-building',   icon: '🌿', color: 'teal' },
              { name: 'Safety',          path: '/kvs-samagam/analytics/safety',            icon: '🛡️', color: 'red' },
              { name: 'Special Labs',    path: '/kvs-samagam/analytics/special-labs',     icon: '🔬', color: 'indigo' },
              { name: 'Water Profile',   path: '/kvs-samagam/analytics/miscellaneous',    icon: '💧', color: 'teal' },
            ].map(item => {
              const isCurrentPage = item.name === 'Water Profile';
              return (
                <button
                  key={item.name}
                  onClick={() => !isCurrentPage && navigate(item.path)}
                  disabled={isCurrentPage}
                  className={`bg-gradient-to-br from-${item.color}-50 to-${item.color}-100 rounded-lg p-4 border border-${item.color}-200 text-center transition-all ${
                    isCurrentPage
                      ? 'opacity-50 cursor-not-allowed border-dashed border-teal-300'
                      : 'hover:shadow-lg cursor-pointer hover:scale-105'
                  }`}
                >
                  <span className="text-3xl">{item.icon}</span>
                  <p className="text-sm font-medium text-gray-700 mt-2">{item.name}</p>
                </button>
              );
            })}
          </div>
        </div>

      </div>
      <Footer />
    </div>
  );
};

export default WaterProfileAnalysis;
